import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  useMediaQuery,
  Alert,
  AlertTitle,
  Paper,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCharactersContext } from "../hooks/Context";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: { optionId: number; option: string; correctAnswer: string | boolean }[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

interface LayoutProps {
  Questions: Question[];
  sectionName: string;
  currentQuestionIndexProp?: number;
}

interface StoryCardItem {
  questionBankId: number;
  text: string;
  targetOrder: number;
  questionObj: Question;
}

const Layout21: React.FC<LayoutProps> = ({
  Questions,
  sectionName,
}) => {
  const {
    questionScores,
    setQuestionScores,
    isViewMode,
    examAnswers,
    setExamAnswers,
  } = useCharactersContext();

  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width: 820px)");
  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);

  const firstQuestion = Questions[0];
  const sentences = firstQuestion?.question ? firstQuestion.question.split("<br>") : [];
  const instructionText = sentences[0] || "Arrange the story in the right order.";

  const [orderedItems, setOrderedItems] = useState<StoryCardItem[]>([]);

  useEffect(() => {
    if (Questions && Questions.length > 0) {
      const parsedItems: StoryCardItem[] = Questions.map((q, idx) => {
        const parts = q.question.split("<br>");
        const rawText = parts.length > 1 ? parts.slice(1).join(" ") : q.question;
        
        // Strip any leading numerical prefixes (e.g. "1. ", "2. ")
        const sentenceText = rawText.replace(/^(\d+[\)\.]\s*)+/g, "").trim();

        let targetOrder = 0;

        // 1. Get sequence value specifically from option item where correctAnswer === true (Correct Option column)
        const correctOpt = q.optionsList?.find(
          (opt) => opt.correctAnswer === true || String(opt.correctAnswer).toLowerCase() === "true"
        );

        if (correctOpt) {
          const match = String(correctOpt.option || "").match(/\d+/);
          if (match) {
            targetOrder = parseInt(match[0], 10);
          }
        }

        // 2. Fallback: Search any option item in optionsList for a digit
        if (!targetOrder || isNaN(targetOrder)) {
          if (q.optionsList && q.optionsList.length > 0) {
            for (const opt of q.optionsList) {
              const match = String(opt.option || "").match(/\d+/);
              if (match) {
                targetOrder = parseInt(match[0], 10);
                break;
              }
            }
          }
        }

        // 3. Fallback to 1-based array index
        if (!targetOrder || isNaN(targetOrder)) {
          targetOrder = idx + 1;
        }

        return {
          questionBankId: q.questionBankId,
          text: sentenceText,
          targetOrder: targetOrder,
          questionObj: q,
        };
      });

      console.log("[Layout21] Initialized parsedItems (from Correct Option):", parsedItems.map(p => ({ qId: p.questionBankId, targetOrder: p.targetOrder, text: p.text.substring(0, 30) })));
      setOrderedItems(parsedItems);
    }
  }, [Questions]);

  useEffect(() => {
    if (isViewMode && orderedItems.length > 0) {
      const sorted = [...orderedItems].sort((a, b) => a.targetOrder - b.targetOrder);
      setOrderedItems(sorted);
    }
  }, [isViewMode]);

  // Real-time automatic syncing of questionScores and examAnswers
  useEffect(() => {
    if (!orderedItems || orderedItems.length === 0 || isViewMode) return;

    const evaluationSummary = orderedItems.map((item, idx) => ({
      currentPos: idx + 1,
      targetOrder: item.targetOrder,
      isCorrect: idx + 1 === item.targetOrder,
      text: item.text.substring(0, 35) + "..."
    }));
    console.log("[Layout21] Current Card Order & Score Evaluation:", evaluationSummary);

    setQuestionScores((prevScores) => {
      const updatedScores = [...prevScores];
      orderedItems.forEach((item, index) => {
        const currentPos = index + 1;
        const isCorrect = currentPos === item.targetOrder;
        const scoreValue = isCorrect ? (item.questionObj.marks || 1) : 0;

        const scoreIndex = updatedScores.findIndex(
          (s) => s.questionBankId === item.questionBankId
        );
        const scoreRecord = {
          questionBankId: item.questionBankId,
          questionPaperId: item.questionObj.questionPaperId,
          score: scoreValue,
          layoutNumber: item.questionObj.uiType,
          sectionName: sectionName,
        };

        if (scoreIndex !== -1) {
          updatedScores[scoreIndex] = scoreRecord;
        } else {
          updatedScores.push(scoreRecord);
        }
      });
      return updatedScores;
    });

    setExamAnswers((prevAnswers) => {
      const updatedExamAnswers = [...prevAnswers];
      orderedItems.forEach((item, index) => {
        const currentPos = index + 1;
        const ansIndex = updatedExamAnswers.findIndex(
          (a) => a.questionBankId === item.questionBankId
        );
        const ansRecord = {
          questionBankId: item.questionBankId,
          acutalOptionValue: `Order ${item.targetOrder}`,
          enteredOptionValue: `Order ${currentPos}`,
        };

        if (ansIndex !== -1) {
          updatedExamAnswers[ansIndex] = ansRecord;
        } else {
          updatedExamAnswers.push(ansRecord);
        }
      });
      return updatedExamAnswers;
    });
  }, [orderedItems, isViewMode, sectionName, setQuestionScores, setExamAnswers]);

  // Move item Up/Down arrow handler
  const moveItem = (index: number, direction: "up" | "down") => {
    if (isViewMode) return;
    const newItems = [...orderedItems];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    const temp = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = temp;
    setOrderedItems(newItems);
  };

  const handleFormSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setShowAlert(true);
    setTimeout(handleAlertClose, 3000);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        px: isMobile ? 1 : 2,
        py: isMobile ? 1.5 : 3,
      }}
    >
      {showAlert && (
        <Alert
          severity="success"
          onClose={handleAlertClose}
          sx={{
            marginBottom: "2vh",
            width: "100%",
            maxWidth: "750px",
            backgroundColor: "#22c55e",
            color: "white",
            borderRadius: "12px",
          }}
        >
          <AlertTitle sx={{ color: "white", fontWeight: "700" }}>
            Story Order Submitted!
          </AlertTitle>
        </Alert>
      )}

      {Questions && Questions.length > 0 && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: isMobile ? "16px" : "20px",
            boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.08)",
            width: "100%",
            maxWidth: isMobile ? "100%" : isTablet ? "90%" : "820px",
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: isMobile ? "16px 12px" : "28px" }}>
            {/* Instruction Header */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: "700",
                fontSize: isMobile ? "1.05rem" : "1.25rem",
                textAlign: "center",
                color: "#0f172a",
                mb: 0.5,
              }}
            >
              {instructionText}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "#64748b",
                mb: isMobile ? 2.5 : 3.5,
                fontSize: isMobile ? "0.82rem" : "0.95rem",
                fontWeight: "450",
              }}
            >
              {isViewMode
                ? "Correct Chronological Story Sequence:"
                : "Use the up (▲) and down (▼) arrows to arrange the story in correct order."}
            </Typography>

            {/* List of Story Sentence Cards */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? 1.5 : 2,
                mb: 3,
              }}
            >
              {orderedItems.map((item, index) => (
                <Paper
                  key={item.questionBankId || index}
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: isMobile ? "12px 10px" : "16px 20px",
                    borderRadius: "14px",
                    border: isViewMode
                      ? "1.5px solid #bbf7d0"
                      : "1.5px solid #e2e8f0",
                    backgroundColor: isViewMode ? "#f0fdf4" : "#ffffff",
                    transition: "all 0.2s ease-in-out",
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.03)",
                    "&:hover": {
                      borderColor: "#3b82f6",
                      boxShadow: "0px 4px 14px rgba(59, 130, 246, 0.12)",
                    },
                  }}
                >
                  {/* Step Number Badge */}
                  <Chip
                    label={index + 1}
                    sx={{
                      backgroundColor: isViewMode ? "#22c55e" : "#2563eb",
                      color: "#ffffff",
                      fontWeight: "700",
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      minWidth: isMobile ? "32px" : "38px",
                      height: isMobile ? "32px" : "38px",
                      borderRadius: "50%",
                      mr: isMobile ? 1.2 : 2,
                      flexShrink: 0,
                    }}
                  />

                  {/* Story Sentence Text */}
                  <Typography
                    sx={{
                      flexGrow: 1,
                      fontSize: isMobile ? "0.88rem" : "1rem",
                      color: "#1e293b",
                      fontWeight: "500",
                      lineHeight: isMobile ? "1.45" : "1.55",
                      wordBreak: "break-word",
                      pr: isMobile ? 0.5 : 1,
                    }}
                  >
                    {item.text}
                  </Typography>

                  {/* Arrow Action Buttons (Move Up / Move Down) */}
                  {!isViewMode && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: isMobile ? 0.5 : 0.6,
                        ml: isMobile ? 0.8 : 1.5,
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => moveItem(index, "up")}
                        disabled={index === 0}
                        aria-label="Move Up"
                        sx={{
                          width: isMobile ? "34px" : "38px",
                          height: isMobile ? "34px" : "38px",
                          borderRadius: "10px",
                          backgroundColor: index === 0 ? "#f1f5f9" : "#eff6ff",
                          color: index === 0 ? "#cbd5e1" : "#2563eb",
                          border: index === 0 ? "1px solid #e2e8f0" : "1px solid #bfdbfe",
                          transition: "all 0.15s ease-in-out",
                          "&:hover": {
                            backgroundColor: index === 0 ? "#f1f5f9" : "#dbeafe",
                          },
                        }}
                      >
                        <KeyboardArrowUpIcon fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => moveItem(index, "down")}
                        disabled={index === orderedItems.length - 1}
                        aria-label="Move Down"
                        sx={{
                          width: isMobile ? "34px" : "38px",
                          height: isMobile ? "34px" : "38px",
                          borderRadius: "10px",
                          backgroundColor: index === orderedItems.length - 1 ? "#f1f5f9" : "#eff6ff",
                          color: index === orderedItems.length - 1 ? "#cbd5e1" : "#2563eb",
                          border: index === orderedItems.length - 1 ? "1px solid #e2e8f0" : "1px solid #bfdbfe",
                          transition: "all 0.15s ease-in-out",
                          "&:hover": {
                            backgroundColor: index === orderedItems.length - 1 ? "#f1f5f9" : "#dbeafe",
                          },
                        }}
                      >
                        <KeyboardArrowDownIcon fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>
                    </Box>
                  )}

                  {isViewMode && (
                    <CheckCircleIcon
                      sx={{
                        color: "#22c55e",
                        ml: 1,
                        fontSize: isMobile ? "1.4rem" : "1.6rem",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Paper>
              ))}
            </Box>

            {/* Submit Button */}
            {!isViewMode && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: isMobile ? 2 : 3 }}>
                <Button
                  variant="contained"
                  onClick={handleFormSubmit}
                  sx={{
                    backgroundColor: "#2563eb",
                    color: "white",
                    fontWeight: "700",
                    px: isMobile ? 4 : 5,
                    py: isMobile ? 1.1 : 1.3,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontSize: isMobile ? "0.92rem" : "1.02rem",
                    width: isMobile ? "100%" : "auto",
                    boxShadow: "0px 6px 16px rgba(37, 99, 235, 0.3)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#1d4ed8",
                      boxShadow: "0px 8px 20px rgba(37, 99, 235, 0.4)",
                    },
                  }}
                >
                  Submit Story Order
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Layout21;
